// lib
import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import '@aytm/lib/common/moment-duration-format'
import { Map } from 'immutable'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { formatCurrency, padRight, unescapeHTML } from '@aytm/lib/common/func'

// components
import LoaderWave from '@aytm/lib/rfd-ui/components/basic/loader-wave'
import PopupHelper from '@aytm/app/survey/editor/components/content/PopupHelper'

// constants
const MAX_DIFFICULTY = 795
const MAX_METER_ANGLE = 268
const LOI_WARNING_TIME = 1440 // in seconds
const METER_TITLES = ['BLITZ', 'STREAMLINED', 'ROBUST', 'ADVANCED', 'SOPHISTICATED', 'SOPHISTICATED']
const loiFormat = loi => moment.duration(loi, 'seconds').format('mm:ss', { trim: false })

// styles
import '@aytm/lib/rfd-ui/styles/advanced/survey-estimator.scss'

// <Component>
SurveyEstimator.propTypes = {
  ir: PropTypes.string,
  loi: PropTypes.number,
  ttl: PropTypes.number,
  hidePrice: PropTypes.bool,
  price: PropTypes.number,
  phone: PropTypes.string,
  status: PropTypes.string.isRequired, // fetching | failed | ready
  message: PropTypes.string,
  panelsMode: PropTypes.oneOf(['blended', 'curated', 'default']),
  showPanelsMode: PropTypes.bool,
  showMeter: PropTypes.bool,
  deliveryBy: PropTypes.number,
  surveyType: PropTypes.string.isRequired,
  difficulty: PropTypes.number,
  respondents: PropTypes.number,
  customQuote: PropTypes.bool,
  customQuoteImage: PropTypes.string,
  targetingsOverlap: PropTypes.bool,
  onPriceClick: PropTypes.func,
  className: PropTypes.string,
  locale: PropTypes.instanceOf(Map).isRequired
}
export default React.memo(
  SurveyEstimator,
  (prevProps, nextProps) =>
    prevProps.ir == nextProps.ir &&
    prevProps.loi == nextProps.loi &&
    prevProps.ttl == nextProps.ttl &&
    prevProps.hidePrice == nextProps.hidePrice &&
    prevProps.price == nextProps.price &&
    prevProps.phone == nextProps.phone &&
    prevProps.status == nextProps.status &&
    prevProps.message == nextProps.message &&
    prevProps.showMeter == nextProps.showMeter &&
    prevProps.panelsMode == nextProps.panelsMode &&
    prevProps.deliveryBy == nextProps.deliveryBy &&
    prevProps.surveyType == nextProps.surveyType &&
    prevProps.difficulty == nextProps.difficulty &&
    prevProps.respondents == nextProps.respondents &&
    prevProps.customQuote == nextProps.customQuote &&
    prevProps.customQuoteImage == nextProps.customQuoteImage &&
    prevProps.targetingsOverlap == nextProps.targetingsOverlap &&
    prevProps.onPriceClick == nextProps.onPriceClick &&
    prevProps.className == nextProps.className &&
    prevProps.locale == nextProps.locale
)
function SurveyEstimator(props, _context) {
  const { ttl, status, locale, showMeter, hidePrice, className, customQuote, surveyType, showPanelsMode } = props

  // delivery guarantee text
  const [ttlText, setTimeToLifeText] = useState(getTtlText(ttl, locale))
  const ttlInterval = useRef(0)
  const ttlCountdown = useRef(ttl ? ttl : 0)
  useEffect(() => {
    ttlCountdown.current = ttl
    if (ttlInterval.current) clearInterval(ttlInterval.current)
    if (0 < ttl) {
      setTimeToLifeText(getTtlText(ttl, locale))
      ttlInterval.current = setInterval(() => {
        const launchWithinSec = Math.max(0, ttlCountdown.current - 60)
        ttlCountdown.current = launchWithinSec

        if (0 == launchWithinSec) {
          if (ttlInterval.current) clearInterval(ttlInterval.current)
          ttlCountdown.current = 0
          ttlInterval.current = 0
          setTimeToLifeText(null)
        } else {
          setTimeToLifeText(getTtlText(launchWithinSec, locale))
        }
      }, 60000)
    } else {
      ttlInterval.current = 0
    }
  }, [locale, ttl])

  useEffect(() => () => ttlInterval.current && clearInterval(ttlInterval.current), [])

  const payload = { ...props, ttlText }
  const componentStyle = classNames('rfd-ui-survey-estimator', className, {
    ['rfd-ui-survey-estimator-custom-quota']: customQuote
  })

  return customQuote || 'failed' == status ? (
    // custom quote
    <div className={componentStyle}>{renderCustomQuote(payload)}</div>
  ) : 'panel' == surveyType ? (
    // panel survey
    <div className={`${componentStyle} rfd-ui-panel-survey-estimator`}>
      {showMeter && renderComplexityMeter(payload)}
      <div
        className={classNames(
          'rfd-ui-survey-estimator-data',
          { ['hidden-price']: hidePrice },
          { ['show-meter']: showMeter }
        )}
      >
        {renderDeliveryDate(payload)}
        {renderPrice(payload)}
        {renderTargetingsOverlap(payload)}
        {renderFooter(payload)}
        {showPanelsMode && renderPanels(payload)}
      </div>
    </div>
  ) : (
    // list survey
    <div className={`${componentStyle} rfd-ui-list-survey-estimator`}>
      <div className={classNames('rfd-ui-survey-estimator-data', 'list-survey')}>
        {renderPrice(payload)}
        {renderFooter(payload)}
      </div>
    </div>
  )
}

function getTtlText(ttl = 0, locale) {
  return ttl ? locale.get('launchTime').replace('{time}', formatLaunchTime(ttl)) : null
}

// loader
function renderLoader() {
  return (
    <div className="rfd-ui-survey-estimator-price-loader">
      <LoaderWave bars={20} height={20} />
    </div>
  )
}

// delivery date
function renderDeliveryDate(payload = {}) {
  const { locale, status, ttlText, hidePrice, deliveryBy } = payload
  return hidePrice && 'fetching' == status ? (
    renderLoader()
  ) : 'failed' != status && deliveryBy && deliveryBy != 0 && ttlText ? (
    <div className="rfd-ui-survey-estimator-delivery-date">
      {ttlText && <div className="delivery-date-note" dangerouslySetInnerHTML={{ __html: ttlText }} />}
      <div className="delivery-date-date">{formatDeliveryDate(deliveryBy, locale)}</div>
    </div>
  ) : null
}
function formatLaunchTime(launchTime) {
  const launchWithinDuration = moment.duration(launchTime, 's')
  return `${launchWithinDuration.hours()}h:${padRight(launchWithinDuration.minutes(), 2, '0')}m`
}
function formatDeliveryDate(date, locale) {
  const deliveryDateLocal = moment.unix(date)
  const currentDateLocal = moment()
  return (
    deliveryDateLocal.format('hh:mm A, ') +
    (() => {
      if (deliveryDateLocal.isSame(currentDateLocal, 'day')) {
        return locale.getIn(['date', 'today'])
      }
      if (deliveryDateLocal.isSame(currentDateLocal.add(1, 'day'), 'day')) {
        return locale.getIn(['date', 'tomorrow'])
      }
      return deliveryDateLocal.format('D MMM').toUpperCase()
    })()
  )
}

// customQuote
function renderCustomQuote(payload = {}) {
  const { phone, status, locale, message, onPriceClick, customQuoteImage } = payload
  const customQuoteLocale = locale.get('customQuote')
  return (
    <div className="rfd-ui-survey-estimator-custom-quote">
      <div className="custom-quote-title">{customQuoteLocale.get('title')}</div>
      {customQuoteImage && <img className="custom-quote-image" src={customQuoteImage} alt="Custom Quote" />}
      {'fetching' == status ? (
        <div className="rfd-ui-survey-estimator-price-loader">
          <LoaderWave bars={20} height={20} />
        </div>
      ) : (
        <div
          className="custom-quote-message"
          dangerouslySetInnerHTML={{ __html: message && '' != message ? message : customQuoteLocale.get('text') }}
        />
      )}
      {phone && <div className="custom-quote-sep" />}
      {phone && (
        <div className="custom-quote-phone" onClick={'function' == typeof onPriceClick ? onPriceClick : null}>
          {phone}
        </div>
      )}
      {renderFooter(payload)}
    </div>
  )
}

// footer
function renderFooter(payload = {}) {
  const { ir, loi, locale, respondents } = payload
  const irLocale = locale.get('ir')
  const loiLocale = locale.get('loi')
  const loiWarning = loi && LOI_WARNING_TIME < loi
  const respondentsLocale = locale.get('respondents')
  return (
    <div className="rfd-ui-survey-estimator-footer">
      <div className="footer-data">
        <div className={classNames('footer-col', 'rfd-ui-survey-estimator-respondents')}>
          <span className="footer-col-label">{respondentsLocale.get('label')}</span>
          <span className="footer-col-value">{respondents || '----'}</span>
        </div>
        <div className={classNames('footer-col', 'rfd-ui-survey-estimator-loi', { warning: loiWarning })}>
          <span className="footer-col-label">{loiLocale.get('label')}</span>
          <span className="footer-col-value">{loi ? loiFormat(loi) : '----'}</span>
        </div>
        <div className={classNames('footer-col', 'rfd-ui-survey-estimator-ir')}>
          <span className="footer-col-label">{irLocale.get('label')}</span>
          <span className="footer-col-value">{unescapeHTML(ir) || '–'}</span>
        </div>
        <div className="footer-hint">
          <div className="rfd-ui-survey-estimator-respondents-hint">
            <span className="footer-hint-label">N</span>
            <span className="footer-hint-text" dangerouslySetInnerHTML={{ __html: respondentsLocale.get('hint') }} />
          </div>
          <div className={classNames('rfd-ui-survey-estimator-loi-hint', { ['footer-hint-warning-row']: loiWarning })}>
            <span className="footer-hint-label">LOI</span>
            <span className="footer-hint-text" dangerouslySetInnerHTML={{ __html: loiLocale.get('hint') }} />
          </div>
          {loiWarning && (
            <div>
              <span className="footer-hint-label loi-label-warning">
                {Math.ceil(loi / 60)}
                <div>min</div>
              </span>
              <span className="footer-hint-text" dangerouslySetInnerHTML={{ __html: loiLocale.get('warning') }} />
            </div>
          )}
          <div className="rfd-ui-survey-estimator-ir-hint">
            <span className="footer-hint-label">IR</span>
            <span className="footer-hint-text" dangerouslySetInnerHTML={{ __html: irLocale.get('hint') }} />
          </div>
        </div>
      </div>
    </div>
  )
}

// total price
function renderPrice(payload = {}) {
  const { price, locale, status, hidePrice, surveyType, onPriceClick } = payload
  return hidePrice ? null : 'fetching' == status || null == price ? (
    renderLoader()
  ) : (
    <div className="rfd-ui-survey-estimator-price">
      <span onClick={'function' == typeof onPriceClick ? onPriceClick : null}>
        ${formatCurrency(price / 100).split('.')[0]}
        <span className="rfd-ui-survey-estimator-price-cents">
          .{formatCurrency(price / 100).split('.')[1] || '00'}
        </span>
      </span>
      <span className="rfd-ui-survey-estimator-price-tax-note">{0 < price ? locale.get('tax') : ''}</span>
      <PopupHelper width="470px" popupOffset={8} direction="righttop">
        {() => <div dangerouslySetInnerHTML={{ __html: locale.getIn(['help', surveyType]) }} />}
      </PopupHelper>
    </div>
  )
}

// targetings overlap
function renderTargetingsOverlap(payload = {}) {
  const { phone, locale, targetingsOverlap } = payload
  return targetingsOverlap ? (
    <div className="rfd-ui-survey-estimator-targetings-overlap">
      <div
        className="targetings-overlap-text"
        dangerouslySetInnerHTML={{ __html: locale.getIn(['targetingsOverlap', 'text']) }}
      />
      <div className="targetings-overlap-phone">{phone}</div>
    </div>
  ) : null
}

function renderPanels(payload = {}) {
  const { panelsMode, locale } = payload
  return 'curated' === panelsMode ? (
    <div className="rfd-ui-survey-estimator-curated-panels">
      <div className="curated-panels-text">{locale.getIn(['curated', 'text'])}</div>
      <PopupHelper width="470px" popupOffset={9} direction="righttop" targetClassName="pendo-help-tm-blended">
        {() => <div dangerouslySetInnerHTML={{ __html: locale.getIn(['curated', 'help']) }} />}
      </PopupHelper>
    </div>
  ) : 'blended' === panelsMode ? (
    <div className="rfd-ui-survey-estimator-blended-panels">
      <div className="blended-panels-text">{locale.getIn(['blended', 'text'])}</div>
      <PopupHelper width="470px" popupOffset={9} direction="righttop" targetClassName="pendo-help-tm-blended">
        {() => <div dangerouslySetInnerHTML={{ __html: locale.getIn(['blended', 'help']) }} />}
      </PopupHelper>
    </div>
  ) : null
}

// meter
function renderComplexityMeter(payload = {}) {
  const { difficulty } = payload
  const arrowAngle = (MAX_METER_ANGLE / MAX_DIFFICULTY) * Math.min(difficulty, MAX_DIFFICULTY)
  const arrowSector = Math.min(Math.max(Math.ceil((arrowAngle - 1) / (MAX_METER_ANGLE / 12)), 1), 12)
  return (
    <div className="rfd-ui-survey-estimator-meter">
      <div className="rfd-ui-survey-estimator-meter-title">SAMPLE COMPLEXITY</div>
      <div className="arrow-circle" />
      <div className="rfd-ui-survey-estimator-meter-arrow-wrap" style={{ transform: `rotateZ(${arrowAngle - 2}deg)` }}>
        <div className={classNames('arrow-shadow', 'arrow-shadow-top', `sector${arrowSector}`)} />
        <div className={classNames('arrow-shadow', 'arrow-shadow-bottom', `sector${arrowSector}`)} />
        <div className="rfd-ui-survey-estimator-meter-arrow" />
      </div>
      <div className="rfd-ui-survey-estimator-meter-subtitle">{METER_TITLES[Math.ceil(arrowSector / 2) - 1]}</div>
    </div>
  )
}
